import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { ComponentsCustomerType } from "@omni/types/components/customer/shared/customerType.enum";
import * as yup from "yup";

import { DefaultAddressValidationSchema } from "@/components/Form/RHF/FormBlocks/Address/partials/defaultAddress.schema";
import { CustomerFormBlockValidationSchema } from "@/components/Form/RHF/FormBlocks/Customer/customerFormBlock.schema";
import { StoreSchema } from "@/components/Form/RHF/FormFields/StoreField/storeField.schema";
import * as RHFAddressValidationRules from "@/utils/validation/schemas/address.RHF";
import * as RHFBonusCardValidationRules from "@/utils/validation/schemas/bonusCard.RHF";
import * as RHFGeneralValidationRules from "@/utils/validation/schemas/general.RHF";
import { string } from "@/utils/validation/schemas/general.RHF";
import * as RHFUserValidationRules from "@/utils/validation/schemas/user.RHF";

import { BoxConditionsValidationSchema } from "./boxConditions/boxConditions.schema";
import { RequiredTermsOfServiceSchema } from "./termsOfService/termsOfServiceCheck.schema";

export const CheckoutCustomerValidationSchema = yup
    .object({
        hasHomeDelivery: yup.boolean().required(),
        receiveSms: yup.boolean().optional(),
        bonusCardAvailable: string().oneOf(["true", "false"]).required(),
        requestInvoice: yup
            .boolean()
            .when(["hasHomeDelivery", "customerType"], {
                is: (
                    hasHomeDelivery?: boolean,
                    customerType?: ComponentsCustomerType
                ) =>
                    !hasHomeDelivery &&
                    customerType !== ComponentsCustomerType.B2B,
                then: (schema) => schema.required(),
                otherwise: (schema) => schema.optional()
            }),
        samePaymentAddress: yup
            .boolean()
            .when(["requestInvoice", "hasHomeDelivery"], {
                is: (requestInvoice?: boolean, hasHomeDelivery?: boolean) =>
                    requestInvoice || hasHomeDelivery,
                then: (schema) => schema.required(),
                otherwise: (schema) => schema.optional()
            }),
        storeId: RHFGeneralValidationRules.storeId,
        bonusCard: yup
            .object({
                number: RHFBonusCardValidationRules.bonusCard
            })
            .optional()
            .default(undefined)
            .when("bonusCardAvailable", {
                is: "true",
                then: (schema) => schema.required(),
                otherwise: (schema) => schema.strip()
            }),
        [AddressType.Delivery]: yup
            .object({
                id: yup.string().optional(),
                addressing: RHFAddressValidationRules.addressing,
                firstName: RHFUserValidationRules.firstName,
                lastName: RHFUserValidationRules.lastName
            })
            .concat(DefaultAddressValidationSchema)
            .when("hasHomeDelivery", {
                is: (v?: boolean) => v,
                then: (schema) => schema.required(),
                otherwise: (schema) => schema.strip()
            })
            .optional()
            .default(undefined),
        [AddressType.Payment]: yup
            .object({
                id: yup.string().optional(),
                firstName: RHFUserValidationRules.firstName,
                lastName: RHFUserValidationRules.lastName
            })
            .concat(DefaultAddressValidationSchema)
            .when(
                [
                    "samePaymentAddress",
                    "requestInvoice",
                    "customerType",
                    "hasHomeDelivery"
                ],
                {
                    is: (
                        samePaymentAddress?: boolean,
                        requestInvoice?: boolean,
                        customerType?: ComponentsCustomerType,
                        hasHomeDelivery?: boolean
                    ) =>
                        (samePaymentAddress === false && hasHomeDelivery) ||
                        (!hasHomeDelivery && requestInvoice) ||
                        (customerType === ComponentsCustomerType.B2B &&
                            !hasHomeDelivery),
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.strip()
                }
            )
            .optional()
            .default(undefined)
    })
    .concat(StoreSchema)
    .concat(RequiredTermsOfServiceSchema)
    .concat(CustomerFormBlockValidationSchema)
    .concat(BoxConditionsValidationSchema);

export type CheckoutCustomerFormValues = yup.InferType<
    typeof CheckoutCustomerValidationSchema
>;
